<template>
  <div>
    <b-overlay :show="formShow" rounded="true" :fixed="true" no-fade>
      <b-card>
        <!-- Page Header -->
        <b-row class="mb-3">
          <b-col cols="12">
            <div class="d-flex justify-content-between align-items-center">
              <h3 class="mb-0 text-primary">
                <feather-icon icon="BarChart3Icon" size="24" class="mr-2" />
                Quality Review Report
              </h3>
              <div class="d-flex">
                <b-button
                  type="submit"
                  @click="showArchiveRecords()"
                  variant="warning"
                  size="md"
                  class="mr-2"
                >
                  {{
                    archive == "N"
                      ? "Show with archive records"
                      : "Hide with archive records"
                  }}
                </b-button>

                <b-button
                  @click="downloadPdf()"
                  v-show="$Can('view_review_report')"
                  variant="outline-primary"
                  size="md"
                  :disabled="!resultData.data.length > 0"
                  class="btn-report text-primary bg-white"
                >
                  <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
                  Generate Report
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- All Filters Section -->
        <b-row class="mb-1">
          <b-col cols="12">
            <b-card class="filter-card compact">
              <b-card-body class="py-2">
                <!-- First Row: Event Selection and Date Range - Always visible -->
                <b-row class="align-items-end mb-1">
                  <b-col xl="4" lg="4" md="6" sm="12" class="mb-1">
                    <b-form-group
                      label="Select Event"
                      label-size="sm"
                      class="mb-0"
                    >
                      <v-select
                        id="event"
                        v-model="selectedEvent"
                        :options="eventTypes"
                        :reduce="(val) => val"
                        :filterable="true"
                        placeholder="Search by event..."
                        @option:selected="selectedEvents"
                        label="title"
                        :clearable="false"
                        input-id="event"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col xl="4" lg="4" md="6" sm="12" class="mb-1">
                    <b-form-group
                      label="Date Range"
                      label-size="sm"
                      class="mb-0"
                    >
                      <b-input-group size="sm">
                        <flat-pickr
                          :disabled="!resultData.data.length"
                          placeholder="Select date range..."
                          v-model="rangeDate"
                          onClose="testClose"
                          class="form-control"
                          :config="{ mode: 'range', dateFormat: 'd/m/Y' }"
                        />
                        <b-input-group-append>
                          <b-button
                            @click="clearDate()"
                            variant="outline-secondary"
                            size="sm"
                            v-b-tooltip.hover
                            title="Clear date filter"
                          >
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col xl="2" lg="2" md="6" sm="6" cols="12" class="mb-1">
                    <b-form-group class="mb-0">
                      <div class="d-flex">
                        <b-button
                          @click="clearAllFilters()"
                          variant="outline-warning"
                          size="sm"
                          :disabled="!hasActiveFilters"
                          class="mr-2"
                        >
                          <feather-icon icon="XIcon" size="12" class="mr-1" />
                          Clear All
                        </b-button>
                        <small
                          class="text-muted d-flex align-items-center"
                          v-if="hasActiveFilters"
                        >
                          <feather-icon
                            icon="FilterIcon"
                            size="12"
                            class="mr-1"
                          />
                          {{ activeFiltersCount }}
                        </small>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Second Row: Data Filters - Only visible when there's data -->
                <b-row
                  class="align-items-end"
                  v-if="resultData.data.length > 0"
                >
                  <b-col xl="2" lg="2" md="3" sm="6" cols="12" class="mb-1">
                    <b-form-group label="Reviewer" label-size="sm" class="mb-0">
                      <v-select
                        v-model="reviewerNameFilter"
                        :options="uniqueReviewers"
                        placeholder="All reviewers..."
                        :clearable="true"
                        :filterable="true"
                        @input="onFilterChange"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col xl="2" lg="2" md="3" sm="6" cols="12" class="mb-1">
                    <b-form-group label="Reviewee" label-size="sm" class="mb-0">
                      <v-select
                        v-model="revieweeNameFilter"
                        :options="uniqueReviewees"
                        placeholder="All reviewees..."
                        :clearable="true"
                        :filterable="true"
                        @input="onFilterChange"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col xl="2" lg="2" md="3" sm="6" cols="12" class="mb-1">
                    <b-form-group label="Section" label-size="sm" class="mb-0">
                      <v-select
                        v-model="groupNameFilter"
                        :options="uniqueGroups"
                        placeholder="All sections..."
                        :clearable="true"
                        :filterable="true"
                        @input="onFilterChange"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col xl="3" lg="2" md="3" sm="6" cols="12" class="mb-1">
                    <b-form-group label="Status" label-size="sm" class="mb-0">
                      <v-select
                        v-model="statusFilter"
                        :options="uniqueStatuses"
                        placeholder="All statuses..."
                        :clearable="true"
                        :filterable="true"
                        @input="onFilterChange"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col xl="2" lg="2" md="3" sm="6" cols="12" class="mb-1">
                    <b-form-group label="Tests" label-size="sm" class="mb-0">
                      <v-select
                        v-model="testTakenCountFilter"
                        :options="uniqueTestCounts"
                        placeholder="All counts..."
                        :clearable="true"
                        :filterable="true"
                        @input="onFilterChange"
                        class="form-select-sm"
                      />
                    </b-form-group>
                  </b-col>

                  <!-- Search Questions field - re-added for filtering -->
                  <!-- <b-col xl="3" lg="2" md="6" sm="6" cols="12" class="mb-1">
                  <b-form-group label="Search Questions" label-size="sm" class="mb-0">
                    <b-input-group size="sm">
                      <b-form-input
                        id="filter-input"
                        v-model="questionNameFilter"
                        type="search"
                        placeholder="Search questions..."
                        @input="onFilterChange"
                        debounce="300"
                        size="sm"
                      />
                      <b-input-group-append v-if="questionNameFilter">
                        <b-button 
                          @click="clearQuestionFilter()" 
                          variant="outline-secondary"
                          size="sm"
                          v-b-tooltip.hover
                          title="Clear search"
                        >
                          <feather-icon icon="XIcon" size="12" />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                -->
                </b-row>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <!-- Results Summary -->
        <b-row class="mb-1" v-if="resultData.data.length > 0">
          <b-col cols="12">
            <small class="text-muted">
              Showing {{ resultData.data.length }} group(s) with
              {{ totalQuestions }} question(s) ({{ resultData.total_surveys }}
              total reviews)
              <span v-if="hasActiveFilters" class="text-info ml-2">
                <feather-icon icon="FilterIcon" size="12" class="mr-1" />
                {{ activeFiltersCount }} filter(s) applied
              </span>
            </small>
          </b-col>
        </b-row>

        <!-- Table Section -->
        <b-row>
          <b-col cols="12" v-if="resultData.data.length > 0">
            <b-overlay :show="tableShow" rounded="true" :fixed="true" no-fade>
              <div
                class="compact-table-container animate__animated animate__fadeIn"
              >
                <!-- No results message -->
                <div
                  v-if="resultData.data.length === 0"
                  class="text-center py-5"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="48"
                    class="text-muted mb-2"
                  />
                  <h5 class="text-muted">No results found</h5>
                  <p class="text-muted">
                    Try adjusting your filters or check if data is available.
                  </p>
                </div>

                <!-- Compact Results Table -->
                <div v-else class="results-table-wrapper">
                  <div class="table-responsive">
                    <b-table
                      :items="filteredResults"
                      :fields="tableFields"
                      striped
                      hover
                      small
                      class="mb-0 compact-results-table"
                      show-empty
                      empty-text="No review results found"
                      :tbody-tr-class="getRowClass"
                    >
                      <!-- Question Name with truncation -->
                      <template #cell(question_name)="data">
                        <div
                          class="question-name-cell"
                          :class="{
                            'group-header': data.item.rowType === 'group',
                            'question-row': data.item.rowType === 'question',
                          }"
                        >
                          <span
                            :title="data.item.question_name"
                            v-b-tooltip.hover
                            :class="{
                              'question-text': true,
                              'group-text font-weight-bold':
                                data.item.rowType === 'group',
                              'question-indent':
                                data.item.rowType === 'question',
                            }"
                          >
                            {{ data.item.question_name }}
                          </span>
                          <div
                            v-if="
                              data.item.comment &&
                                data.item.rowType === 'question'
                            "
                            class="mt-1"
                          >
                            <small class="text-muted">{{
                              data.item.comment
                            }}</small>
                          </div>
                        </div>
                      </template>

                      <!-- Score with conditional formatting -->
                      <template #cell(reviewee_score)="data">
                        <span
                          v-if="
                            data.item.reviewee_score !== '' &&
                              data.item.reviewee_score !== null
                          "
                          class="score-badge"
                          :class="{
                            'bg-success text-white':
                              data.item.status === 'Passed',
                            'bg-danger text-white':
                              data.item.status === 'Failed',
                            'bg-secondary text-white':
                              data.item.status !== 'Passed' &&
                              data.item.status !== 'Failed',
                            'group-score': data.item.rowType === 'group',
                          }"
                        >
                          {{ data.item.reviewee_score || 0 }}
                        </span>
                      </template>

                      <!-- Max Score -->
                      <template #cell(max_score_of_question)="data">
                        <span
                          v-if="
                            data.item.max_score_of_question !== '' &&
                              data.item.max_score_of_question !== null
                          "
                        >
                          {{ data.item.max_score_of_question }}
                        </span>
                      </template>

                      <!-- Passing Score -->
                      <template #cell(passing_score)="data">
                        <span
                          v-if="
                            data.item.passing_score !== '' &&
                              data.item.passing_score !== null
                          "
                          class="passing-score-badge"
                          :class="{
                            'font-weight-bold': data.item.rowType === 'group',
                          }"
                        >
                          {{ data.item.passing_score }}
                        </span>
                      </template>

                      <!-- Status with badge -->
                      <template #cell(status)="data">
                        <div v-if="data.item.status" class="status-badges">
                          <b-badge
                            :variant="getStatusVariant(data.item.status)"
                            :class="{
                              'status-badge': true,
                              'group-status': data.item.rowType === 'group',
                            }"
                          >
                            {{ data.item.status }}
                          </b-badge>
                          <b-badge
                            v-if="
                              data.item.rowType === 'question' &&
                                data.item.compulsory === 'Yes'
                            "
                            variant="warning"
                            class="compulsory-badge ml-1"
                          >
                            Compulsory
                          </b-badge>
                        </div>
                      </template>

                      <!-- Success Rate with percentage -->
                      <template #cell(success_rate_of_question)="data">
                        <div
                          v-if="data.item.success_rate_of_question"
                          class="success-rate-cell"
                        >
                          <span class="success-rate-text">{{
                            data.item.success_rate_of_question
                          }}</span>
                        </div>
                      </template>

                      <!-- Test Taken Count -->
                      <template #cell(test_taken_count)="data">
                        <b-badge
                          v-if="data.item.test_taken_count"
                          variant="light-primary"
                          class="test-count-badge"
                        >
                          {{ data.item.test_taken_count || 0 }}
                        </b-badge>
                      </template>

                      <!-- Completion Time/Date -->
                      <template #cell(completion_time)="data">
                        <span
                          v-if="data.item.completion_time"
                          class="completion-date"
                        >
                          {{ data.item.completion_time }}
                        </span>
                      </template>
                    </b-table>
                  </div>

                  <!-- Pagination -->
                  <div
                    class="d-flex justify-content-between align-items-center mt-3"
                  >
                    <div>
                      <small class="text-muted">
                        Showing {{ resultData.data.length }} group(s) with
                        {{ totalQuestions }} question(s)
                      </small>
                    </div>
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      :hide-goto-end-buttons="false"
                      :prev-text="'Previous'"
                      :next-text="'Next'"
                      :first-text="'First'"
                      :last-text="'Last'"
                      size="sm"
                      class="mb-0"
                      @change="onPageChange"
                    />
                    <div>
                      <b-form-group class="mb-0 d-flex align-items-center">
                        <label class="mr-2 mb-0 small">Groups per page:</label>
                        <b-form-select
                          v-model="perPage"
                          :options="[5, 10, 25, 50]"
                          size="sm"
                          style="width: auto;"
                          @change="onPerPageChange"
                        />
                      </b-form-group>
                    </div>
                  </div>
                </div>
              </div>
            </b-overlay>
          </b-col>

          <b-col cols="12" v-else>
            <div class="text-center py-5">
              <feather-icon
                icon="AlertCircleIcon"
                size="64"
                class="text-muted mb-3"
              />
              <h3 class="text-muted">No Results Available</h3>
              <p class="text-muted">
                There are no results for this record. Please try a different
                selection.
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <template #overlay v-if="download">
        <div class="text-center">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon"> </b-icon>
          <p id="cancel-label">
            <feather-icon icon="DownloadIcon" /> Report creating please wait...
          </p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCol,
  BRow,
  BTab,
  BTabs,
  BCard,
  BCardHeader,
  BCardBody,
  BAlert,
  BLink,
  BIcon,
  BProgress,
  BPagination,
  BFormSelect,
  VBModal,
} from "bootstrap-vue";
import { ref, onUnmounted } from "@vue/composition-api";

import store from "@/store";
import reviewStoreModule from "./reviewStoreModule";
import ImageCropper from "@core/components/image-cropper/ImageCropper.vue";
import { required, alphaNum, email } from "@validations";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import Cleave from "vue-cleave-component";
import "cleave.js/dist/addons/cleave-phone.us";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axiosIns from "@/libs/axios";
import _ from "lodash";
import { t } from "@/@core/libs/i18n/utils";
import { backgroundColor } from "echarts/lib/theme/dark";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { $themeColors } from "@themeConfig";
import "animate.css";
import moment from "moment";

export default {
  components: {
    BTab,
    BTabs,
    BCard,
    BCardHeader,
    BCardBody,
    BAlert,
    BLink,
    BIcon,
    BProgress,
    BRow,
    BCol,
    BPagination,
    BFormSelect,
    vSelect,
    Cleave,
    ToastificationContent,
    ImageCropper,
    ValidationProvider,
    ValidationObserver,
    flatPickr,
  },

  directives: {
    Ripple,
    "b-modal": VBModal,
  },

  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = "review";
    // Register module
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, reviewStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },

  data: function() {
    return {
      resultData: {
        data: [],
        total_records: 0,
        total_reviews: 0,
        message: "",
      },
      rangeDate: null,
      filterDateStart: null,
      filterDateEnd: null,
      baseURL: store.state.app.baseURL,
      required,
      alphaNum,
      email,
      dropdownLoading: false,
      download: false,
      formShow: false,
      tableShow: false,
      selectedEvent: null,
      eventTypes: [],
      archive: "N",

      // Search filters
      reviewerNameFilter: "",
      revieweeNameFilter: "",
      groupNameFilter: "",
      questionNameFilter: "",
      statusFilter: "",
      testTakenCountFilter: "",

      // Filter debouncing
      filterDebounceTimeout: null,

      // Pagination
      currentPage: 1,
      perPage: 10,
      totalRows: 0,

      // Filter options from API
      filterOptions: {
        reviewers: [],
        reviewees: [],
        sections: [],
        statuses: [],
        test_taken_counts: [],
      },

      // Table fields for compact display
      tableFields: [
        {
          key: "reviewer_name",
          label: "Reviewer",
          sortable: true,
          thStyle: { width: "10%" },
        },
        {
          key: "reviewee_name",
          label: "Reviewee",
          sortable: true,
          thStyle: { width: "10%" },
        },
        {
          key: "group_name",
          label: "Group",
          sortable: true,
          thStyle: { width: "12%" },
        },
        {
          key: "question_name",
          label: "Question",
          sortable: true,
          thStyle: { width: "20%" },
        },
        {
          key: "reviewee_score",
          label: "Score",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "6%" },
        },
        {
          key: "max_score_of_question",
          label: "Max",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "5%" },
        },
        {
          key: "passing_score",
          label: "Passing Score",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "8%" },
        },
        {
          key: "status",
          label: "Status",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "7%" },
        },
        {
          key: "success_rate_of_question",
          label: "Success Rate",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "8%" },
        },
        {
          key: "test_taken_count",
          label: "Tests",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "5%" },
        },
        {
          key: "completion_time",
          label: "Date",
          class: "text-center",
          thClass: "text-center",
          sortable: true,
          thStyle: { width: "9%" },
        },
      ],
    };
  },

  computed: {
    filteredResults() {
      // Since we're now doing server-side filtering, just return the flattened data
      return this.flattenedTableData || [];
    },

    flattenedTableData() {
      const flattened = [];

      if (!this.resultData.data || this.resultData.data.length === 0) {
        return [];
      }

      this.resultData.data.forEach((group, groupIndex) => {
        // Add group header row
        const groupRow = {
          id: `group-${groupIndex}`,
          rowType: "group",
          reviewer_name: group.reviewer_name,
          reviewee_name: group.reviewee_name,
          group_name: group.group_name,
          question_name: `${group.group_name} (${group.questions?.length ||
            0} questions)`,
          reviewee_score: group.questions_scores_summed,
          max_score_of_question: "",
          passing_score: group.passing_score_group,
          status: group.status,
          success_rate_of_question: group.success_rate || "",
          test_taken_count: group.test_taken_count,
          completion_time: this.formatDate(group.completion_time),
          _rowVariant: "group-header",
        };

        flattened.push(groupRow);

        // Add individual question rows
        if (group.questions && group.questions.length > 0) {
          group.questions.forEach((question, questionIndex) => {
            const questionRow = {
              id: `question-${groupIndex}-${questionIndex}`,
              rowType: "question",
              reviewer_name: "", // Empty for question rows to avoid repetition
              reviewee_name: "", // Empty for question rows to avoid repetition
              group_name: "", // Empty for question rows to avoid repetition
              question_name: question.question_name,
              reviewee_score: question.reviewee_score,
              max_score_of_question: question.max_score_of_question,
              passing_score:
                question.passing_score || question.min_score_of_question,
              status: question.status,
              success_rate_of_question: question.success_rate_of_question,
              test_taken_count: question.test_taken_count,
              completion_time: "", // Empty for question rows
              comment: question.comment,
              compulsory: question.compulsory,
              _rowVariant: "question-row",
            };

            flattened.push(questionRow);
          });
        }
      });

      return flattened;
    },

    hasActiveFilters() {
      return !!(
        this.reviewerNameFilter ||
        this.revieweeNameFilter ||
        this.groupNameFilter ||
        // this.questionNameFilter ||  // Commented out since field is disabled
        this.statusFilter ||
        this.testTakenCountFilter
      );
    },

    activeFiltersCount() {
      let count = 0;
      if (this.reviewerNameFilter) count++;
      if (this.revieweeNameFilter) count++;
      if (this.groupNameFilter) count++;
      // if (this.questionNameFilter) count++;  // Commented out since field is disabled
      if (this.statusFilter) count++;
      if (this.testTakenCountFilter) count++;
      return count;
    },

    uniqueReviewers() {
      return this.filterOptions.reviewers || [];
    },

    uniqueReviewees() {
      return this.filterOptions.reviewees || [];
    },

    uniqueGroups() {
      return this.filterOptions.sections || [];
    },

    uniqueStatuses() {
      return this.filterOptions.statuses || [];
    },

    uniqueTestCounts() {
      return this.filterOptions.test_taken_counts || [];
    },

    totalQuestions() {
      if (!this.resultData.data || this.resultData.data.length === 0) {
        return 0;
      }

      return this.resultData.data.reduce((total, group) => {
        return total + (group.questions ? group.questions.length : 0);
      }, 0);
    },
  },

  mounted() {
    this.getReviewResults();
  },

  created() {},

  watch: {
    rangeDate: {
      handler: function(val, before) {
        if (val != null) {
          if (val.includes("to")) {
            this.filterDateStart = this.rangeDate.split(" to ")[0];
            this.filterDateEnd = this.rangeDate.split(" to ")[1];

            this.selectedEvents();
          }
        }
      },
    },
  },

  methods: {
    onFilterChange() {
      // Debounce filter changes to avoid too many API calls
      if (this.filterDebounceTimeout) {
        clearTimeout(this.filterDebounceTimeout);
      }

      this.filterDebounceTimeout = setTimeout(() => {
        this.applyFiltersAndFetchData();
      }, 500);
    },

    applyFiltersAndFetchData() {
      // Reset to first page when filters change
      this.currentPage = 1;
      if (this.selectedEvent) {
        this.selectedEvents(this.selectedEvent);
      }
    },

    clearQuestionFilter() {
      this.questionNameFilter = "";
      this.onFilterChange();
    },

    clearAllFilters() {
      this.reviewerNameFilter = "";
      this.revieweeNameFilter = "";
      this.groupNameFilter = "";
      // this.questionNameFilter = "";  // Commented out since field is disabled
      this.statusFilter = "";
      this.testTakenCountFilter = "";

      // Reset pagination to first page
      this.currentPage = 1;

      // Trigger API call to refresh data without filters
      this.applyFiltersAndFetchData();
    },

    getStatusVariant(status) {
      if (status === "Passed") return "success";
      if (status === "Failed") return "danger";
      return "secondary";
    },

    showArchiveRecords() {
      if (this.archive == "Y") {
        this.archive = "N";
        this.selectedEvents();
      } else {
        this.archive = "Y";
        this.selectedEvents();
      }
    },

    downloadPdf() {
      this.formShow = true;
      this.download = true;

      let info = {
        surveyName: this.selectedEvent.title,
        date: moment(new Date()).format("DD/MM/YYYY"),
        totalRecords: this.resultData.total_records,
        totalReviews: this.resultData.total_surveys,
        filteredRecords: this.resultData.data.length,
      };

      let requestData = {
        surveyId: this.selectedEvent.id,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
        archive: this.archive,
        filters: {
          reviewerName: this.reviewerNameFilter || null,
          revieweeName: this.revieweeNameFilter || null,
          groupName: this.groupNameFilter || null,
          status: this.statusFilter || null,
          testTakenCount: this.testTakenCountFilter || null,
        },
        info: info,
      };

      store
        .dispatch("review/createReviewReportPdf", requestData)
        .then((response) => {
          if (response.status == 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Review Report.pdf");
            document.body.appendChild(link);
            link.click();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Successful",
                text: "✔️ Report is created!",
                icon: "ThumbsUpIcon",
                variant: "success",
              },
            });
            this.download = false;
            this.formShow = false;
          } else {
            this.formShow = false;
            this.download = false;
          }
        })
        .catch((error) => {
          this.formShow = false;
          this.download = false;
        });
    },

    getLastEvent() {
      store
        .dispatch("review/getLastEventItem", [])
        .then((res) => {
          if (res.data) {
            this.selectedEvent = res.data;
            this.selectedEvents(res.data);
          }
        })
        .catch((error) => {
          console.log(error);
          this.formShow = false;
        });
    },

    clearDate() {
      if (this.rangeDate != null && this.rangeDate != "") {
        this.filterDateStart = null;
        this.filterDateEnd = null;
        this.rangeDate = null;
        this.selectedEvents();
      }
    },

    getReviewResults() {
      this.formShow = true;

      let filter = {
        event: this.selectedEvent ? this.selectedEvent : null,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
      };

      store
        .dispatch("review/getAllReviewResults", filter)
        .then((res) => {
          console.log(res.data);
          if (res.data.length > 0) {
            this.summaryList = res.data[0].summary;
            this.getLastEvent();
            res.data.forEach((element) => {
              this.eventTypes.push(element.event);
            });

            this.eventTypes = _.uniqBy(this.eventTypes, function(e) {
              return e.id;
            });

            // Store filter options if available in the initial response
            if (res.data[0] && res.data[0].filter_options) {
              this.filterOptions = {
                reviewers: res.data[0].filter_options.reviewers || [],
                reviewees: res.data[0].filter_options.reviewees || [],
                sections: res.data[0].filter_options.sections || [],
                statuses: res.data[0].filter_options.statuses || [],
                test_taken_counts:
                  res.data[0].filter_options.test_taken_counts || [],
              };
            }
          }

          this.formShow = false;
        })
        .catch((error) => {
          this.formShow = false;
          console.log(error);
        });
    },

    selectedEvents(event) {
      this.tableShow = true;

      let filter = {
        event: event == undefined ? this.selectedEvent : event,
        filterDate: {
          startDate: this.filterDateStart,
          endDate: this.filterDateEnd,
        },
        archive: this.archive,
        filters: {
          reviewerName: this.reviewerNameFilter || null,
          revieweeName: this.revieweeNameFilter || null,
          groupName: this.groupNameFilter || null,
          // questionName: this.questionNameFilter || null,  // Commented out since field is disabled
          status: this.statusFilter || null,
          testTakenCount: this.testTakenCountFilter || null,
        },
        pagination: {
          page: this.currentPage,
          perPage:
            this.perPage > this.resultData.total_records ? this.perPage : 25,
        },
      };

      store
        .dispatch("review/getResultsWithEventIdTable", filter)
        .then((res) => {
          if (res.data.message && res.data.message.includes("successfully")) {
            this.resultData = {
              data: res.data.data || [],
              total_records: res.data.total_count || 0,
              total_reviews: res.data.total_surveys || 0,
              message: res.data.message || "",
            };
            // Set totalRows to the number of groups, not individual questions
            this.totalRows = res.data.data ? res.data.data.length : 0;

            // Store filter options from API response
            if (res.data.filter_options) {
              this.filterOptions = {
                reviewers: res.data.filter_options.reviewers || [],
                reviewees: res.data.filter_options.reviewees || [],
                sections: res.data.filter_options.sections || [],
                statuses: res.data.filter_options.statuses || [],
                test_taken_counts:
                  res.data.filter_options.test_taken_counts || [],
              };
            }
          } else {
            this.resultData = {
              data: [],
              total_records: 0,
              total_reviews: 0,
              message: "No data found",
            };
            this.totalRows = 0;
          }
          this.tableShow = false;
          this.formShow = false;
        })
        .catch((error) => {
          this.formShow = false;
          this.resultData = {
            data: [],
            total_records: 0,
            total_reviews: 0,
            message: "Error loading data",
          };
          this.totalRows = 0;
          console.log(error);
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.selectedEvents();
    },

    onPerPageChange(perPage) {
      this.perPage = perPage;
      this.selectedEvents();
    },

    formatDate(date) {
      if (date) {
        // Try to parse the date - backend might send in MM-DD-YYYY format
        const parsedDate = moment(
          String(date),
          ["MM-DD-YYYY", "M-D-YYYY", "YYYY-MM-DD"],
          true
        );
        if (parsedDate.isValid()) {
          return parsedDate.format("DD/MM/YYYY");
        }
        // Fallback to default parsing if specific format fails
        return moment(date).format("DD/MM/YYYY");
      }
      return "";
    },

    getRowClass(item) {
      if (item.rowType === "group") {
        return "table-group-header";
      } else if (item.rowType === "question") {
        return "table-question-row";
      }
      return "";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.left-align .nav-tabs .nav-link {
  justify-content: left !important;
}

.nav-pills .nav-link {
  margin-bottom: 7px;
  white-space: nowrap;
}

/* Statistical Data Card Styling */
.statistical-data-card {
  .card-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 1px solid #dee2e6;
    border-radius: 0.375rem 0.375rem 0 0 !important;
    padding: 0.75rem 1rem;

    h6 {
      color: #495057 !important;
      font-size: 0.95rem;
    }
  }

  .stats-row {
    margin: 0;
    padding: 0.5rem 0;
  }

  .stat-col {
    padding: 0.25rem 0.5rem;
  }

  .stat-item-compact {
    border-radius: 0.375rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background-color: rgba(0, 123, 255, 0.03);
      transform: translateY(-1px);
      border-color: rgba(0, 123, 255, 0.1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .stat-icon-wrapper {
      width: 15px;
      height: 15px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 0.5rem;
    }

    h5 {
      font-weight: 700;
      font-size: 1.1rem;
      line-height: 1.2;
    }

    .stat-label {
      font-weight: 500;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      line-height: 1.2;
    }
  }
}

/* Groups Container Styling */
.groups-container {
  .question-content {
    h6 {
      line-height: 1.4;
      margin-bottom: 0.25rem;
    }

    p {
      line-height: 1.3;
      margin-bottom: 0;
    }
  }
}

/* Card header styling */
.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 0.375rem 0.375rem 0 0 !important;

  h5 {
    color: #495057 !important;
  }

  .badge {
    &.badge-light-info {
      background-color: rgba(23, 162, 184, 0.1) !important;
      color: #17a2b8 !important;
      border: 1px solid rgba(23, 162, 184, 0.2);
    }

    &.badge-light-success {
      background-color: rgba(40, 167, 69, 0.1) !important;
      color: #28a745 !important;
      border: 1px solid rgba(40, 167, 69, 0.2);
    }

    &.badge-light-primary {
      background-color: rgba(0, 123, 255, 0.1) !important;
      color: #007bff !important;
      border: 1px solid rgba(0, 123, 255, 0.2);
    }
  }
}

/* Score status colors */
.bg-light-success {
  background-color: rgba(40, 167, 69, 0.1) !important;
  color: #28a745 !important;
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
  color: #dc3545 !important;
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
  color: #ffc107 !important;
}

/* Empty state styling */
.text-center {
  .feather-icon {
    opacity: 0.5;
  }
}

/* Purple color variants */
.text-purple {
  color: #6f42c1 !important;
}

.bg-light-purple {
  background-color: rgba(111, 66, 193, 0.1) !important;
}

.bg-light-secondary {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

/* Table row styling for failing scores */
.table-danger-row {
  background-color: rgba(220, 53, 69, 0.1) !important;

  &:hover {
    background-color: rgba(220, 53, 69, 0.15) !important;
  }

  td {
    border-top-color: rgba(220, 53, 69, 0.2) !important;
  }
}

/* Controls Section Styling */
.form-select-lg {
  .vs__dropdown-toggle {
    min-height: 21px;
    border: 1px solid #d8d6de;
    border-radius: 0.375rem;

    &:hover {
      border-color: #b8b6c1;
    }
  }

  .vs__selected-options {
    padding: 2px 12px;
  }
}

.btn-report {
  min-height: 38px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.15);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    transform: none;
    box-shadow: none;
  }
}

/* Success Rate Progress Bar Styling */
.success-rate-container {
  min-width: 120px;
  max-width: 150px;

  .progress {
    border-radius: 10px;
    background-color: #f8f9fa;

    .progress-bar {
      border-radius: 10px;
      font-size: 0.75rem;
      font-weight: 600;

      &.bg-success {
        background: linear-gradient(90deg, #28a745 0%, #20c997 100%) !important;
      }

      &.bg-warning {
        background: linear-gradient(90deg, #ffc107 0%, #fd7e14 100%) !important;
      }

      &.bg-info {
        background: linear-gradient(90deg, #17a2b8 0%, #6610f2 100%) !important;
      }

      &.bg-danger {
        background: linear-gradient(90deg, #dc3545 0%, #e83e8c 100%) !important;
      }
    }
  }

  small {
    font-size: 0.7rem;
    line-height: 1.2;
  }
}

/* Clean Filter Styling to match ReviewList */
.mb-40 {
  margin-bottom: 40px;
}

/* Filter Card Styling */
.filter-card {
  border: 1px solid #e3e6f0;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
  border-radius: 0.35rem;

  &.compact {
    .card-body {
      padding: 0.75rem 1rem;
    }
  }

  .card-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 1px solid #e3e6f0;
    padding: 0.5rem 1rem;

    h6 {
      color: #5a5c69;
      font-weight: 600;
      margin: 0;
      font-size: 0.875rem;
    }
  }

  .card-body {
    padding: 1rem;
  }

  /* Compact form group styling */
  .form-group {
    margin-bottom: 0.5rem;

    label {
      font-weight: 500;
      color: #5a5c69;
      font-size: 0.75rem;
      margin-bottom: 0.25rem;
    }
  }

  /* v-select compact styling */
  .v-select.form-select-sm {
    .vs__dropdown-toggle {
      border: 1px solid #d1d3e2;
      border-radius: 0.25rem;
      min-height: 32px;

      &:hover {
        border-color: #a0a0a0;
      }

      &.vs--open {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
      }
    }

    .vs__selected-options {
      padding: 2px 8px;
    }

    .vs__search {
      font-size: 0.8rem;
      padding: 0;
      margin: 0;
    }

    .vs__selected {
      font-size: 0.8rem;
      color: #5a5c69;
      padding: 0;
      margin: 0;
    }

    .vs__actions {
      padding: 2px 8px;
    }
  }

  /* Input styling consistency */
  .form-control {
    border: 1px solid #d1d3e2;
    border-radius: 0.25rem;
    font-size: 0.8rem;

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.25);
    }
  }

  .btn-outline-warning {
    border-color: #f6c23e;
    color: #f6c23e;
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;

    &:hover {
      background-color: #f6c23e;
      border-color: #f6c23e;
      color: #fff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* Compact Table Styling */
.compact-table-container {
  .results-table-wrapper {
    background: white;
    border-radius: 0.375rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .compact-results-table {
    font-size: 0.875rem;

    thead th {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 2px solid #dee2e6;
      font-weight: 600;
      font-size: 0.8rem;
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
    }

    tbody td {
      padding: 0.6rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #f1f1f1;
    }

    tbody tr:hover {
      background-color: rgba(0, 123, 255, 0.04);
    }

    /* Group Header Row Styling */
    tbody tr.table-group-header {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-top: 2px solid #007bff;
      font-weight: 600;

      &:hover {
        background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
      }

      td {
        padding: 0.8rem 0.5rem;
        border-top: 2px solid #007bff;
        font-weight: 600;
      }
    }

    /* Question Row Styling */
    tbody tr.table-question-row {
      background-color: #fdfdfd;

      &:hover {
        background-color: rgba(0, 123, 255, 0.06);
      }

      td {
        padding: 0.5rem 0.5rem;
        border-top: 1px solid #f8f9fa;
        border-left: 3px solid #e9ecef;
      }
    }
  }

  .question-name-cell {
    &.group-header {
      .group-text {
        color: #495057;
        font-size: 0.9rem;
        display: flex;
        align-items: center;

        &:before {
          content: "📂";
          margin-right: 0.5rem;
          font-size: 0.8rem;
        }
      }
    }

    &.question-row {
      .question-indent {
        padding-left: 1.5rem;
        position: relative;

        &:before {
          content: "↳";
          position: absolute;
          left: 0.5rem;
          color: #6c757d;
          font-size: 0.8rem;
        }
      }
    }

    .question-text {
      display: block;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.4;
      font-weight: 500;
    }
  }

  .score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-weight: 600;
    font-size: 0.875rem;
    min-width: 40px;
    text-align: center;

    &.group-score {
      font-size: 0.9rem;
      font-weight: 700;
      padding: 0.3rem 0.6rem;
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
    }
  }

  .passing-score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    background-color: #e9ecef;
    color: #495057;
    font-size: 0.8rem;
    font-weight: 500;

    &.font-weight-bold {
      font-weight: 700;
      background-color: #ffc107;
      color: #212529;
    }
  }

  .status-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.35rem 0.6rem;

    &.group-status {
      font-size: 0.8rem;
      padding: 0.4rem 0.7rem;
      font-weight: 700;
    }
  }

  .status-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    justify-content: center;

    .compulsory-badge {
      font-size: 0.65rem;
      font-weight: 600;
      padding: 0.25rem 0.4rem;
      background-color: #ffc107 !important;
      color: #212529 !important;
      border: 1px solid #e0a800;
      white-space: nowrap;
    }
  }

  .success-rate-cell {
    .success-rate-text {
      font-weight: 500;
      color: #495057;
    }
  }

  .test-count-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
  }

  .completion-date {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 500;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-card {
    .card-body {
      padding: 1rem 0.75rem;
    }
  }

  .compact-results-table {
    thead th,
    tbody td {
      padding: 0.5rem 0.25rem;
      font-size: 0.8rem;
    }

    .question-name-cell .question-text {
      max-width: 200px;
    }
  }
}

/* Pagination Styling */
.pagination {
  .page-link {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
    border-color: #d1d3e2;
    color: #5a5c69;

    &:hover {
      background-color: #f8f9fa;
      border-color: #d1d3e2;
      color: #5a5c69;
    }
  }

  .page-item.active .page-link {
    background-color: #007bff;
    border-color: #007bff;
  }

  .page-item.disabled .page-link {
    background-color: #f8f9fa;
    border-color: #d1d3e2;
    color: #6c757d;
  }
}

/* Results Table Wrapper Pagination */
.results-table-wrapper {
  .pagination-info {
    font-size: 0.8rem;
    color: #6c757d;
  }
}

/* Page Header Styling */
.text-primary h3 {
  color: #007bff !important;
  font-weight: 600;
}

.page-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}
</style>
